import PropTypes from 'prop-types';
import { useMemo, useEffect, useReducer, useCallback } from 'react';

import axios, { endpoints } from 'src/utils/axios';

import { AuthContext } from './auth-context';

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const invitationTokenName = 'invitationToken';
const patientIdTokenName = 'patientId';

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
      teams: action.payload.teams,
      currentTeam: action.payload.currentTeam,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
      teams: action.payload.teams,
      currentTeam: action.payload.currentTeam,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
      teams: action.payload.teams,
      currentTeam: action.payload.currentTeam,
    };
  }
  if (action.type === 'FORGET_PASSWORD') {
    return {
      ...state,
      userTemp: action.payload.userTemp,
    };
  }
  if (action.type === 'MAGIC_CODE') {
    return {
      ...state,
      userMagic: action.payload.userMagic,
    };
  }
  if (action.type === 'TEAM') {
    return {
      ...state,
      currentTeam: action.payload.currentTeam,
      user: action.payload.user,
    };
  }
  if (action.type === 'INVITATION_TOKEN') {
    return {
      ...state,
      teamInvitationToken: action.payload.teamInvitationToken,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      loading: false,
      user: null,
      teams: null,
      currentTeam: null,
      currentPatientId: null,
      userMagic: null,
      userTemp: null,
      teamInvitationToken: null,
      authenticated: false,
      unauthenticated: true,
    };
  }
  if (action.type === 'APPEND_TEAM') {
    return {
      ...state,
      teams: [
        ...state.teams,
        {
          team: action.payload.team,
          role: action.payload.role,
          isCreator: action.payload.isCreator,
          id: action.payload.id,
        },
      ], // Add the new team
    };
  }
  if (action.type === 'REMOVE_TEAM') {
    return {
      ...state,
      teams: state.teams.filter((team) => team.team.id !== action.payload.teamId),
    };
  }
  if (action.type === 'UPDATE_TEAM') {
    return {
      ...state,
      teams: state.teams.map((team) =>
        team.team.id === action.payload.teamId ? { ...team, team: action.payload.team } : team
      ),
    };
  }
  if (action.type === 'PATIENT') {
    return {
      ...state,
      currentPatientId: action.payload.currentPatientId,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const response = await axios.get(endpoints.auth.me);

      const { user, message } = response.data;

      if (!user) {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
            teams: null,
            currentTeam: null,
          },
        });
        throw new Error(message);
      } else {
        const teamsResponse = await axios.get(endpoints.team.getMyTeams);

        const { teams } = teamsResponse.data;

        dispatch({
          type: 'INITIAL',
          payload: {
            user: {
              ...user,
              currentTeamRole: teams?.[0]?.role,
              isCreatorOfCurrentTeam: teams?.[0]?.isCreator,
              currentTeamMembershipId: teams?.[0]?.id,
              // Set current subscription id to the current team
              currentSubscriptionId: teams?.[0]?.team?.subscriptionId,
            },
            teams,
            // Set the first team as the current team
            currentTeam: teams?.[0]?.team,
          },
        });
      }
    } catch (error) {
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
          teams: null,
          currentTeam: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password, token) => {
    const data = {
      email,
      password,
      token,
    };

    const response = await axios.post(endpoints.auth.login, data);

    const { user } = response.data;

    const teamsResponse = await axios.get(endpoints.team.getMyTeams);

    const { teams } = teamsResponse.data;

    dispatch({
      type: 'LOGIN',
      payload: {
        user: {
          ...user,
          currentTeamRole: teams?.[0]?.role,
          isCreatorOfCurrentTeam: teams?.[0]?.isCreator,
          currentTeamMembershipId: teams?.[0]?.id,
          // Set current subscription id to the current team
          currentSubscriptionId: teams?.[0]?.team?.subscriptionId,
        },
        teams,
        // Set the first team as the current team
        currentTeam: teams?.[0]?.team,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName, token) => {
    const data = {
      email,
      password,
      name: `${firstName} ${lastName}`,
      token,
    };

    const response = await axios.post(endpoints.auth.register, data);

    const { user } = response.data;

    const teamsResponse = await axios.get(endpoints.team.getMyTeams);

    const { teams } = teamsResponse.data;

    dispatch({
      type: 'REGISTER',
      payload: {
        user: {
          ...user,
          currentTeamRole: teams?.[0]?.role,
          isCreatorOfCurrentTeam: teams?.[0]?.isCreator,
          currentTeamMembershipId: teams?.[0]?.id,
          // Set current subscription id to the current team
          currentSubscriptionId: teams?.[0]?.team?.subscriptionId,
        },
        teams,
        // Set the first team as the current team
        currentTeam: teams?.[0]?.team,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    await axios.post(endpoints.auth.logout);
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  // VERIFY THE EMAIL WITH OTP
  const verifyEmail = useCallback(async (otp) => {
    const data = {
      code: otp,
    };
    await axios.post(endpoints.auth.verifyEmail, data);
  }, []);

  // RESEND THE OTP
  const resendOtp = useCallback(async () => {
    await axios.post(endpoints.auth.resendVerificationCode);
  }, []);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email) => {
    const data = {
      email,
    };

    await axios.post(endpoints.auth.forgetPassword, data);

    dispatch({
      type: 'FORGET_PASSWORD',
      payload: {
        userTemp: {
          email,
        },
      },
    });
  }, []);

  // FORGOT PASSWORD OTP VERIFY
  const forgetPasswordOtpVerify = useCallback(async (email, code) => {
    const data = {
      code,
      email,
    };

    await axios.post(endpoints.auth.forgetPasswordOtp, data);

    dispatch({
      type: 'FORGET_PASSWORD',
      payload: {
        userTemp: {
          email,
          code,
        },
      },
    });
  }, []);

  // NEW PASSWORD
  const updatePassword = useCallback(async (email, password, code) => {
    const data = {
      code,
      email,
      password,
    };

    const response = await axios.post(endpoints.auth.updatePassword, data);

    const { user } = response.data;

    const teamsResponse = await axios.get(endpoints.team.getMyTeams);

    const { teams } = teamsResponse.data;

    dispatch({
      type: 'LOGIN',
      payload: {
        user: {
          ...user,
          currentTeamRole: teams?.[0]?.role,
          isCreatorOfCurrentTeam: teams?.[0]?.isCreator,
          currentTeamMembershipId: teams?.[0]?.id,
          // Set current subscription id to the current team
          currentSubscriptionId: teams?.[0]?.team?.subscriptionId,
        },
        userTemp: null,
        teams,
        // Set the first team as the current team
        currentTeam: teams?.[0]?.team,
      },
    });
  }, []);

  // REQUEST MAGIC CODE
  const requestMagicCode = useCallback(async (email) => {
    const data = {
      email,
    };

    await axios.post(endpoints.auth.requestMagicCode, data);

    dispatch({
      type: 'MAGIC_CODE',
      payload: {
        userMagic: {
          email,
        },
      },
    });
  }, []);

  // LOGIN WITH MAGIC CODE
  const loginWithMagicCode = useCallback(async (email, code, token) => {
    const data = {
      code,
      email,
      token,
    };

    const response = await axios.post(endpoints.auth.magicCode, data);

    const { user } = response.data;

    const teamsResponse = await axios.get(endpoints.team.getMyTeams);

    const { teams } = teamsResponse.data;

    dispatch({
      type: 'LOGIN',
      payload: {
        user: {
          ...user,
          currentTeamRole: teams?.[0]?.role,
          isCreatorOfCurrentTeam: teams?.[0]?.isCreator,
          currentTeamMembershipId: teams?.[0]?.id,
          // Set current subscription id to the current team
          currentSubscriptionId: teams?.[0]?.team?.subscriptionId,
        },
        userMagic: null,
        teams,
        // Set the first team as the current team
        currentTeam: teams?.[0]?.team,
      },
    });
  }, []);

  // LOGIN WITH GITHUB
  const loginWithGithub = useCallback(async (token) => {
    const response = await axios.get(`${endpoints.auth.github}${token ? `?token=${token}` : ''}`);
    const { url } = response.data;
    // Redirect to the URL
    window.location.href = url;
  }, []);

  // LOGIN WITH GOOGLE
  const loginWithGoogle = useCallback(async (token) => {
    const response = await axios.get(`${endpoints.auth.google}${token ? `?token=${token}` : ''}`);
    const { url } = response.data;
    // Redirect to the URL
    window.location.href = url;
  }, []);

  // SET TEAM INVITATION TOKEN
  const setTeamInvitationToken = useCallback((token) => {
    // Set the token in the session storage
    sessionStorage.setItem(invitationTokenName, token);

    dispatch({
      type: 'INVITATION_TOKEN',
      payload: {
        teamInvitationToken: token,
      },
    });
  }, []);

  // REMOVE TEAM INVITATION TOKEN
  const removeTeamInvitationToken = useCallback(() => {
    // Remove the token from the session storage
    sessionStorage.removeItem(invitationTokenName);

    dispatch({
      type: 'INVITATION_TOKEN',
      payload: {
        teamInvitationToken: null,
      },
    });
  }, []);

  // SET CURRENT TEAM
  const setCurrentTeam = useCallback(
    (team, object) => {
      const { user } = state;
      dispatch({
        type: 'TEAM',
        payload: {
          currentTeam: team,
          user: {
            ...user,
            currentTeamRole: object?.role || null,
            isCreatorOfCurrentTeam: object?.isCreator || null,
            currentTeamMembershipId: object?.id || null,
            // Set current subscription id to the current team
            currentSubscriptionId: team?.subscriptionId || null,
          },
        },
      });
    },
    [state]
  );

  // APPEND A TEAM TO THE TEAMS LIST
  const appendTeam = useCallback(
    (team, role, isCreator, id) => {
      dispatch({
        type: 'APPEND_TEAM',
        payload: {
          team, // Send the new team directly
          role,
          isCreator,
          id,
        },
      });
    },
    [] // Include `state` in dependencies to ensure it reflects the latest value
  );

  // REMOVE A TEAM FROM THE TEAMS LIST
  const removeTeam = useCallback(
    (teamId) => {
      dispatch({
        type: 'REMOVE_TEAM',
        payload: {
          teamId,
        },
      });
    },
    [] // Include `state` in dependencies to ensure it reflects the latest value
  );

  // UPDATE A TEAM IN THE TEAMS LIST
  const updateTeam = useCallback(
    (teamId, team) => {
      dispatch({
        type: 'UPDATE_TEAM',
        payload: {
          teamId,
          team,
        },
      });
    },
    [] // No need to include `state` in dependencies as you're not directly relying on its value
  );

  // SET CURRENT PATIENT
  const setCurrentPatientId = useCallback(
    (id) => {
      // Set the token in the session storage
      sessionStorage.setItem(patientIdTokenName, id);
      dispatch({
        type: 'PATIENT',
        payload: {
          currentPatientId: id,
        },
      });
    },
    [] // No need to include `state` in dependencies as you're not directly relying on its value
  );

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      userTemp: state.userTemp,
      userMagic: state.userMagic,
      teamInvitationToken: state.teamInvitationToken || sessionStorage.getItem(invitationTokenName),
      teams: state.teams,
      currentTeam: state.currentTeam,
      currentPatientId: state.currentPatientId || sessionStorage.getItem(patientIdTokenName),
      method: 'supabase',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      // Functions
      login,
      requestMagicCode,
      loginWithMagicCode,
      loginWithGithub,
      loginWithGoogle,
      register,
      logout,
      forgotPassword,
      updatePassword,
      verifyEmail,
      resendOtp,
      forgetPasswordOtpVerify,
      setCurrentTeam,
      appendTeam,
      removeTeam,
      updateTeam,
      setTeamInvitationToken,
      removeTeamInvitationToken,
      setCurrentPatientId,
    }),
    [
      state.user,
      state.userTemp,
      state.userMagic,
      state.teamInvitationToken,
      state.teams,
      state.currentTeam,
      state.currentPatientId,
      status,
      login,
      requestMagicCode,
      loginWithMagicCode,
      loginWithGithub,
      loginWithGoogle,
      register,
      logout,
      forgotPassword,
      updatePassword,
      verifyEmail,
      resendOtp,
      forgetPasswordOtpVerify,
      setCurrentTeam,
      appendTeam,
      removeTeam,
      updateTeam,
      setTeamInvitationToken,
      removeTeamInvitationToken,
      setCurrentPatientId,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
